/**
 * Deterministic compact near-square chart layout.
 *
 * Priority: no card/card overlaps → clean edge routing (no edge-through-card) →
 * readable edge labels → compact roughly-square AABB.
 *
 * Pure LLM path trusts LLM for entities/edges only — final x/y comes from
 * {@link layoutChartHubSquare}, not from move-entity pixel coords.
 */

import {getArrowBindings} from 'tldraw';
import {
    ENTITY_CARD_MAX_HEIGHT,
    ENTITY_CARD_SHAPE_TYPE,
    ENTITY_CARD_WIDTH,
} from '../tldraw/customShapes/entityCardConstants';
import {
    countCardInteriorEdgeHits,
    countEdgeCrossings,
    estimateMinEdgeLengthForLabel,
} from './chartArrowRouting';
import {
    applyChartEdgeStyle,
    arrowLabelTextFromShape,
    dedupeFanInArrowLabels,
    normalizeEntityRef,
    resolveEntityCardOverlaps,
    rerouteAllChartArrows,
    shapeRect,
} from './applyChartMutations';
/** Match applyChartMarkupMutations.PLACEMENT_GAP — inlined to avoid a cycle. */
const PLACEMENT_GAP = 90;

/**
 * Hub-square / tidy layout gaps: wider horizontally, tighter vertically.
 * @param {number} [gap=PLACEMENT_GAP]
 * @returns {{gapH: number, gapV: number}}
 */
export function layoutCardGaps(gap = PLACEMENT_GAP) {
    return {
        gapH: Math.round(gap * 1.15),
        gapV: Math.round(gap * 0.85),
    };
}
/** Cap how far ensureReadableEdgeLengths may push a card from its start position. */
export const MAX_EDGE_LENGTH_PUSH = 220;
/** AABB max/min side above this → treat as a wide/tall strip and force hub pack. */
export const STRIP_ASPECT_RATIO = 1.6;

/** @typedef {{id: string, x: number, y: number, w: number, h: number}} CardRect */
/** @typedef {{fromId: string, toId: string, label: string}} CardEdge */

/**
 * @param {import('tldraw').Editor} editor
 * @returns {CardRect[]}
 */
export function collectEntityCards(editor) {
    const cards = [];
    for (const shape of editor?.getCurrentPageShapes?.() || []) {
        if (shape?.type !== ENTITY_CARD_SHAPE_TYPE) continue;
        const rect = shapeRect(editor, shape.id);
        if (!rect) continue;
        cards.push({
            id: shape.id,
            x: rect.x,
            y: rect.y,
            w: rect.w || ENTITY_CARD_WIDTH,
            h: rect.h || ENTITY_CARD_MAX_HEIGHT,
            ref: normalizeEntityRef(shape.props?.wikidataId) || null,
            label: String(shape.props?.label || shape.props?.name || ''),
        });
    }
    return cards;
}

/**
 * @param {{w: number, h: number}} bounds
 * @returns {number} max(w,h) / min(w,h); 1 = square
 */
export function boundingBoxAspectRatio(bounds) {
    const w = Math.max(bounds?.w || 0, 1);
    const h = Math.max(bounds?.h || 0, 1);
    return Math.max(w, h) / Math.min(w, h);
}

/**
 * True when the card AABB is a long strip (wider or taller than ~1.6:1).
 * @param {CardRect[]} cards
 * @param {number} [threshold]
 * @returns {boolean}
 */
export function isStripLayout(cards, threshold = STRIP_ASPECT_RATIO) {
    if (!cards?.length) return false;
    return boundingBoxAspectRatio(cardsBoundingBox(cards)) > threshold;
}

/**
 * Degree map keyed by card shape id.
 * @param {CardRect[]} cards
 * @param {CardEdge[]} edges
 * @returns {Map<string, number>}
 */
export function cardDegreeById(cards, edges) {
    const degree = new Map(cards.map((c) => [c.id, 0]));
    const known = new Set(degree.keys());
    for (const edge of edges || []) {
        if (known.has(edge.fromId)) degree.set(edge.fromId, (degree.get(edge.fromId) || 0) + 1);
        if (known.has(edge.toId)) degree.set(edge.toId, (degree.get(edge.toId) || 0) + 1);
    }
    return degree;
}

/**
 * Pick hub: explicit ref → goal-keyword label match → highest degree → first card.
 *
 * @param {Array<CardRect & {ref?: string|null, label?: string}>} cards
 * @param {CardEdge[]} edges
 * @param {{hubRef?: string|null, goalKeywords?: string[]}} [options]
 * @returns {string|null} shape id
 */
export function pickChartHubId(cards, edges, options = {}) {
    if (!cards?.length) return null;
    const degree = cardDegreeById(cards, edges);
    const byBestDegree = (list) => [...list].sort((a, b) => {
        const d = (degree.get(b.id) || 0) - (degree.get(a.id) || 0);
        if (d !== 0) return d;
        return String(a.id).localeCompare(String(b.id));
    })[0];

    const hubRef = normalizeEntityRef(options.hubRef);
    if (hubRef) {
        const match = cards.find((c) => normalizeEntityRef(c.ref) === hubRef);
        if (match) return match.id;
    }

    const keywords = (options.goalKeywords || [])
        .map((k) => String(k || '').trim().toLowerCase())
        .filter(Boolean);
    if (keywords.length) {
        const matched = cards.filter((c) => {
            const label = String(c.label || '').toLowerCase();
            return keywords.some((k) => label.includes(k));
        });
        if (matched.length) return byBestDegree(matched).id;
    }

    return byBestDegree(cards).id;
}

/**
 * Infer a hub entity ref from markup ops (add-arrow endpoints / near anchors).
 * @param {Array<{type?: string, from?: string, to?: string, near?: string, ref?: string}>} ops
 * @returns {string|null}
 */
export function inferHubRefFromMarkupOps(ops) {
    const degree = new Map();
    const bump = (ref, weight = 1) => {
        const key = normalizeEntityRef(ref);
        if (!key) return;
        degree.set(key, (degree.get(key) || 0) + weight);
    };
    for (const op of ops || []) {
        if (op?.type === 'add-arrow') {
            bump(op.from, 1);
            bump(op.to, 1);
        } else if (op?.type === 'add-entity' && op.near) {
            bump(op.near, 0.5);
        }
    }
    let best = null;
    let bestScore = -1;
    for (const [ref, score] of degree) {
        if (score > bestScore) {
            bestScore = score;
            best = ref;
        }
    }
    return best;
}

/**
 * Order satellites BFS from hub (graph-aware), then leftover by id.
 * @param {CardRect[]} cards
 * @param {CardEdge[]} edges
 * @param {string} hubId
 * @returns {CardRect[]}
 */
function orderSatellitesFromHub(cards, edges, hubId) {
    const byId = new Map(cards.map((c) => [c.id, c]));
    const adj = new Map(cards.map((c) => [c.id, new Set()]));
    for (const edge of edges || []) {
        if (!byId.has(edge.fromId) || !byId.has(edge.toId)) continue;
        adj.get(edge.fromId)?.add(edge.toId);
        adj.get(edge.toId)?.add(edge.fromId);
    }
    const ordered = [];
    const seen = new Set([hubId]);
    const queue = [hubId];
    while (queue.length) {
        const id = queue.shift();
        const neighbors = [...(adj.get(id) || [])].sort((a, b) => a.localeCompare(b));
        for (const n of neighbors) {
            if (seen.has(n)) continue;
            seen.add(n);
            queue.push(n);
            const card = byId.get(n);
            if (card) ordered.push(card);
        }
    }
    for (const card of cards) {
        if (seen.has(card.id)) continue;
        ordered.push(card);
    }
    return ordered;
}

/**
 * True when almost every edge touches `hubId` (star / hub-spoke).
 * @param {CardRect[]} cards
 * @param {CardEdge[]} edges
 * @param {string} hubId
 * @returns {boolean}
 */
export function isHubSpokeTopology(cards, edges, hubId) {
    if (!hubId || !cards?.length || cards.length < 3) return false;
    if (!edges?.length) return false;
    const degree = cardDegreeById(cards, edges);
    const hubDeg = degree.get(hubId) || 0;
    if (hubDeg < Math.max(2, Math.ceil((cards.length - 1) * 0.6))) return false;
    return edges.every((e) => e.fromId === hubId || e.toId === hubId);
}

/**
 * Longest minimum edge length required by any edge directly touching the
 * hub — used to widen the first ring so hub→spoke labels have room, instead
 * of relying solely on the post-layout {@link ensureReadableEdgeLengths}
 * shove to fix it up asymmetrically after the fact.
 *
 * @param {CardEdge[]} edges
 * @param {string} hubId
 * @returns {number}
 */
function longestHubSpokeMinLength(edges, hubId) {
    let maxLen = 0;
    for (const edge of edges || []) {
        if (edge.fromId !== hubId && edge.toId !== hubId) continue;
        const len = estimateMinEdgeLengthForLabel(edge.label);
        if (len > maxLen) maxLen = len;
    }
    return maxLen;
}

/**
 * Circular multi-ring positions (clear radial spokes for hub-spoke charts).
 * Ring capacity is capped so one flat ring cannot stretch into a wide strip.
 *
 * @param {CardRect[]} cards
 * @param {string} hubId
 * @param {CardEdge[]} edges
 * @param {number} gap
 * @param {number} centerX
 * @param {number} centerY
 * @param {number} [radiusBoost=0]
 * @returns {Array<{id: string, x: number, y: number}>}
 */
export function computeHubRingPositions(
    cards,
    hubId,
    edges,
    gap,
    centerX,
    centerY,
    radiusBoost = 0,
) {
    const hub = cards.find((c) => c.id === hubId);
    if (!hub) return [];

    const positions = [{
        id: hubId,
        x: centerX - hub.w / 2,
        y: centerY - hub.h / 2,
    }];
    const satellites = orderSatellitesFromHub(cards, edges, hubId);
    if (satellites.length === 0) return positions;

    const maxW = Math.max(...cards.map((c) => c.w), ENTITY_CARD_WIDTH);
    const maxH = Math.max(...cards.map((c) => c.h), ENTITY_CARD_MAX_HEIGHT);
    const {gapH, gapV} = layoutCardGaps(gap);
    const slot = Math.max(maxW + gapH, maxH + gapV) + radiusBoost;
    // Ring 1's spoke length is roughly (radius - hub half-extent - card
    // half-extent), i.e. the plain gap — widen it to whatever the longest
    // hub-touching label needs so spokes don't get squeezed short only to
    // be shoved apart asymmetrically afterward by ensureReadableEdgeLengths.
    const spokeGap = Math.max(Math.max(gapH, gapV), longestHubSpokeMinLength(edges, hubId));
    const hubClearance = Math.max(hub.w, hub.h) / 2 + spokeGap + Math.max(maxW, maxH) / 2;

    let placed = 0;
    let ring = 0;
    while (placed < satellites.length) {
        ring += 1;
        const radius = hubClearance + (ring - 1) * slot + radiusBoost;
        const chord = Math.max(maxW + gapH, maxH + gapV);
        const circularCap = Math.max(4, Math.floor((2 * Math.PI * radius) / chord));
        // Cap early rings so cards spill outward (compact near-square AABB).
        const squareCap = Math.max(6, 8 * ring);
        const capacity = Math.min(circularCap, squareCap);
        const batch = satellites.slice(placed, placed + capacity);
        const n = batch.length;
        batch.forEach((card, index) => {
            const angle = (2 * Math.PI * index) / n - Math.PI / 2;
            positions.push({
                id: card.id,
                x: centerX + Math.cos(angle) * radius - card.w / 2,
                y: centerY + Math.sin(angle) * radius - card.h / 2,
            });
        });
        placed += n;
    }
    return positions;
}

/**
 * Square lattice with hub in the center cell (general graphs / anti-strip).
 *
 * @param {CardRect[]} cards
 * @param {string} hubId
 * @param {CardEdge[]} edges
 * @param {number} gap
 * @param {number} centerX
 * @param {number} centerY
 * @param {number} [radiusBoost=0] extra px added to cell size (overlap retries)
 * @returns {Array<{id: string, x: number, y: number}>}
 */
function computeHubGridPositions(
    cards,
    hubId,
    edges,
    gap,
    centerX,
    centerY,
    radiusBoost = 0,
) {
    const hub = cards.find((c) => c.id === hubId);
    if (!hub) return [];

    const satellites = orderSatellitesFromHub(cards, edges, hubId);
    if (satellites.length === 0) {
        return [{
            id: hubId,
            x: centerX - hub.w / 2,
            y: centerY - hub.h / 2,
        }];
    }

    const maxW = Math.max(...cards.map((c) => c.w), ENTITY_CARD_WIDTH);
    const maxH = Math.max(...cards.map((c) => c.h), ENTITY_CARD_MAX_HEIGHT);
    const {gapH, gapV} = layoutCardGaps(gap);
    const cellW = maxW + gapH + radiusBoost;
    const cellH = maxH + gapV + radiusBoost;
    const n = satellites.length + 1;

    let side = Math.max(3, Math.ceil(Math.sqrt(n)));
    if (side % 2 === 0) side += 1;
    while (side * side < n) side += 2;

    let cols = side;
    let rows = side;
    const footprintAspect = () => (cols * cellW) / Math.max(rows * cellH, 1);
    while (footprintAspect() > 1.25 && cols > 1 && (cols - 1) * rows >= n) {
        cols -= 1;
    }
    while (footprintAspect() < 0.8 && rows > 1 && cols * (rows - 1) >= n) {
        rows -= 1;
    }
    if (cols % 2 === 0 && (cols + 1) * rows >= n) cols += 1;
    if (rows % 2 === 0 && cols * (rows + 1) >= n) rows += 1;

    const hubCol = Math.floor((cols - 1) / 2);
    const hubRow = Math.floor((rows - 1) / 2);
    const originX = centerX - (hubCol + 0.5) * cellW;
    const originY = centerY - (hubRow + 0.5) * cellH;

    /** @type {Array<{col: number, row: number}>} */
    const cells = [];
    for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
            if (col === hubCol && row === hubRow) continue;
            cells.push({col, row});
        }
    }
    cells.sort((a, b) => {
        const da = Math.max(Math.abs(a.col - hubCol), Math.abs(a.row - hubRow));
        const db = Math.max(Math.abs(b.col - hubCol), Math.abs(b.row - hubRow));
        if (da !== db) return da - db;
        if (a.row !== b.row) return a.row - b.row;
        return a.col - b.col;
    });

    const positions = [{
        id: hubId,
        x: originX + hubCol * cellW + (cellW - hub.w) / 2,
        y: originY + hubRow * cellH + (cellH - hub.h) / 2,
    }];

    satellites.forEach((card, index) => {
        const cell = cells[index];
        if (!cell) return;
        positions.push({
            id: card.id,
            x: originX + cell.col * cellW + (cellW - card.w) / 2,
            y: originY + cell.row * cellH + (cellH - card.h) / 2,
        });
    });
    return positions;
}

/**
 * Hub-centric positions with a near-square footprint.
 * Hub-spoke graphs use capped multi-rings (clear spokes); otherwise a centered square grid.
 *
 * @param {CardRect[]} cards
 * @param {string} hubId
 * @param {CardEdge[]} edges
 * @param {number} gap
 * @param {number} centerX
 * @param {number} centerY
 * @param {number} [radiusBoost=0]
 * @returns {Array<{id: string, x: number, y: number}>}
 */
export function computeHubSquarePositions(
    cards,
    hubId,
    edges,
    gap = PLACEMENT_GAP,
    centerX,
    centerY,
    radiusBoost = 0,
) {
    if (isHubSpokeTopology(cards, edges, hubId)) {
        return computeHubRingPositions(cards, hubId, edges, gap, centerX, centerY, radiusBoost);
    }
    return computeHubGridPositions(cards, hubId, edges, gap, centerX, centerY, radiusBoost);
}

/**
 * @param {import('tldraw').Editor} editor
 * @returns {CardEdge[]}
 */
export function collectCardEdges(editor) {
    const edges = [];
    for (const shape of editor?.getCurrentPageShapes?.() || []) {
        if (shape?.type !== 'arrow') continue;
        const bindings = getArrowBindings(editor, shape) || {};
        const fromId = bindings.start?.toId;
        const toId = bindings.end?.toId;
        if (!fromId || !toId) continue;
        edges.push({
            fromId,
            toId,
            label: arrowLabelTextFromShape(shape) || '',
        });
    }
    return edges;
}

/**
 * @param {CardRect[]} cards
 * @returns {{minX: number, minY: number, maxX: number, maxY: number, w: number, h: number}}
 */
export function cardsBoundingBox(cards) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const card of cards) {
        minX = Math.min(minX, card.x);
        minY = Math.min(minY, card.y);
        maxX = Math.max(maxX, card.x + card.w);
        maxY = Math.max(maxY, card.y + card.h);
    }
    if (!Number.isFinite(minX)) {
        return {minX: 0, minY: 0, maxX: 0, maxY: 0, w: 0, h: 0};
    }
    return {minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY};
}

/**
 * @param {CardRect[]} cards
 * @param {number} pad
 * @returns {boolean}
 */
export function cardsHaveOverlaps(cards, pad) {
    for (let i = 0; i < cards.length; i += 1) {
        const a = cards[i];
        for (let j = i + 1; j < cards.length; j += 1) {
            const b = cards[j];
            const separate = (
                a.x + a.w + pad <= b.x
                || b.x + b.w + pad <= a.x
                || a.y + a.h + pad <= b.y
                || b.y + b.h + pad <= a.y
            );
            if (!separate) return true;
        }
    }
    return false;
}

/**
 * Reading-order sort preserving relative arrangement.
 * @param {CardRect[]} cards
 * @returns {CardRect[]}
 */
export function sortCardsReadingOrder(cards) {
    return [...cards].sort((a, b) => {
        const rowSlack = Math.max(40, Math.min(a.h, b.h) * 0.35);
        if (Math.abs(a.y - b.y) > rowSlack) return a.y - b.y;
        return a.x - b.x;
    });
}

/**
 * BFS from highest-degree node so connected cards pack into neighboring cells.
 * @param {CardRect[]} cards
 * @param {CardEdge[]} edges
 * @returns {CardRect[]}
 */
export function sortCardsGraphAware(cards, edges) {
    if (!cards.length) return [];
    if (!edges?.length) return sortCardsReadingOrder(cards);

    const byId = new Map(cards.map((c) => [c.id, c]));
    /** @type {Map<string, Set<string>>} */
    const adj = new Map();
    for (const card of cards) adj.set(card.id, new Set());
    for (const edge of edges) {
        if (!byId.has(edge.fromId) || !byId.has(edge.toId)) continue;
        adj.get(edge.fromId)?.add(edge.toId);
        adj.get(edge.toId)?.add(edge.fromId);
    }

    const degree = (id) => adj.get(id)?.size || 0;
    const seeds = [...cards].sort((a, b) => {
        const d = degree(b.id) - degree(a.id);
        if (d !== 0) return d;
        return a.id.localeCompare(b.id);
    });

    const ordered = [];
    const seen = new Set();
    for (const seed of seeds) {
        if (seen.has(seed.id)) continue;
        const queue = [seed.id];
        seen.add(seed.id);
        while (queue.length) {
            const id = queue.shift();
            const card = byId.get(id);
            if (card) ordered.push(card);
            const neighbors = [...(adj.get(id) || [])].sort((a, b) => {
                const d = degree(b) - degree(a);
                if (d !== 0) return d;
                return a.localeCompare(b);
            });
            for (const n of neighbors) {
                if (seen.has(n)) continue;
                seen.add(n);
                queue.push(n);
            }
        }
    }
    return ordered;
}

/**
 * Straight center-to-center segments for scoring (pre-routing).
 * @param {CardRect[]} cards
 * @param {CardEdge[]} edges
 * @returns {Array<{fromId: string, toId: string, points: Array<{x: number, y: number}>}>}
 */
export function straightEdgePolylines(cards, edges) {
    const byId = new Map(cards.map((c) => [c.id, c]));
    const out = [];
    for (const edge of edges || []) {
        const from = byId.get(edge.fromId);
        const to = byId.get(edge.toId);
        if (!from || !to) continue;
        out.push({
            fromId: edge.fromId,
            toId: edge.toId,
            points: [
                {x: from.x + from.w / 2, y: from.y + from.h / 2},
                {x: to.x + to.w / 2, y: to.y + to.h / 2},
            ],
        });
    }
    return out;
}

/**
 * Count center-line crossings + edge-through-other-card hits.
 * @param {CardRect[]} cards
 * @param {CardEdge[]} edges
 * @returns {{crossings: number, cardHits: number}}
 */
export function scoreStraightEdgeMess(cards, edges) {
    const polylines = straightEdgePolylines(cards, edges);
    let crossings = 0;
    for (let i = 0; i < polylines.length; i += 1) {
        const a = polylines[i];
        const others = polylines.slice(i + 1).filter((b) => (
            b.fromId !== a.fromId
            && b.fromId !== a.toId
            && b.toId !== a.fromId
            && b.toId !== a.toId
        ));
        crossings += countEdgeCrossings(a.points, others);
    }
    let cardHits = 0;
    for (const card of cards) {
        const foreign = polylines.filter((p) => p.fromId !== card.id && p.toId !== card.id);
        cardHits += countCardInteriorEdgeHits(card, foreign);
    }
    return {crossings, cardHits};
}

/**
 * Pack cards into a near-square grid, preferring graph-aware order when edges exist.
 * @param {CardRect[]} cards
 * @param {number} [gap]
 * @param {CardEdge[]} [edges]
 * @returns {Array<{id: string, x: number, y: number}>}
 */
export function packCardsNearSquare(cards, gap = PLACEMENT_GAP, edges = []) {
    const ordered = edges?.length
        ? sortCardsGraphAware(cards, edges)
        : sortCardsReadingOrder(cards);
    const n = ordered.length;
    if (n === 0) return [];

    const cols = Math.max(1, Math.ceil(Math.sqrt(n)));
    const {gapH, gapV} = layoutCardGaps(gap);
    const cellW = Math.max(...ordered.map((c) => c.w), ENTITY_CARD_WIDTH) + gapH;
    const cellH = Math.max(...ordered.map((c) => c.h), ENTITY_CARD_MAX_HEIGHT) + gapV;

    const bounds = cardsBoundingBox(ordered);
    const cx = bounds.minX + bounds.w / 2;
    const cy = bounds.minY + bounds.h / 2;
    const rows = Math.max(1, Math.ceil(n / cols));
    const gridW = cols * cellW - gapH;
    const gridH = rows * cellH - gapV;
    const originX = cx - gridW / 2;
    const originY = cy - gridH / 2;

    return ordered.map((card, index) => {
        const col = index % cols;
        const row = Math.floor(index / cols);
        return {
            id: card.id,
            x: originX + col * cellW,
            y: originY + row * cellH,
        };
    });
}

/**
 * Greedy adjacent swaps on a packed grid to cut straight-edge crossings.
 * @param {Array<{id: string, x: number, y: number}>} positions
 * @param {CardRect[]} cards
 * @param {CardEdge[]} edges
 * @param {number} [maxSwaps]
 * @returns {Array<{id: string, x: number, y: number}>}
 */
export function reducePackCrossingsBySwaps(positions, cards, edges, maxSwaps = 48) {
    if (!positions?.length || !edges?.length) return positions || [];
    const byId = new Map(cards.map((c) => [c.id, c]));
    const pos = positions.map((p) => ({...p}));

    const asCards = () => pos.map((p) => {
        const src = byId.get(p.id);
        return {
            id: p.id,
            x: p.x,
            y: p.y,
            w: src?.w || ENTITY_CARD_WIDTH,
            h: src?.h || ENTITY_CARD_MAX_HEIGHT,
        };
    });

    let bestScore = scoreStraightEdgeMess(asCards(), edges);
    let improved = true;
    let swaps = 0;
    while (improved && swaps < maxSwaps) {
        improved = false;
        for (let i = 0; i < pos.length; i += 1) {
            for (let j = i + 1; j < pos.length; j += 1) {
                const a = pos[i];
                const b = pos[j];
                // Only swap near-neighbors (same row/col adjacency by distance).
                const dist = Math.hypot(a.x - b.x, a.y - b.y);
                const cell = Math.max(ENTITY_CARD_WIDTH, ENTITY_CARD_MAX_HEIGHT) + PLACEMENT_GAP;
                if (dist > cell * 1.35) continue;

                const ax = a.x;
                const ay = a.y;
                a.x = b.x;
                a.y = b.y;
                b.x = ax;
                b.y = ay;
                const next = scoreStraightEdgeMess(asCards(), edges);
                const better = (
                    next.crossings + next.cardHits * 2
                    < bestScore.crossings + bestScore.cardHits * 2
                );
                if (better) {
                    bestScore = next;
                    improved = true;
                    swaps += 1;
                } else {
                    b.x = a.x;
                    b.y = a.y;
                    a.x = ax;
                    a.y = ay;
                }
                if (swaps >= maxSwaps) break;
            }
            if (swaps >= maxSwaps) break;
        }
    }
    return pos;
}

/**
 * Prefer local repulsion only when the chart is already compact and edges are clean.
 * @param {CardRect[]} cards
 * @param {number} gap
 * @param {CardEdge[]} [edges]
 * @returns {boolean}
 */
export function shouldPreferLocalRepulsion(cards, gap = PLACEMENT_GAP, edges = []) {
    if (cards.length < 2) return true;
    const bounds = cardsBoundingBox(cards);
    const aspect = bounds.w > 0 && bounds.h > 0 ? bounds.h / bounds.w : 1;
    if (cardsHaveOverlaps(cards, gap)) return false;
    if (aspect < 0.45 || aspect > 2.2) return false;

    if (edges?.length) {
        const mess = scoreStraightEdgeMess(cards, edges);
        if (mess.cardHits > 0 || mess.crossings > 0) return false;
    }

    const packedPositions = packCardsNearSquare(cards, gap, edges);
    const byId = new Map(cards.map((c) => [c.id, c]));
    const packedCards = packedPositions.map((pos) => {
        const src = byId.get(pos.id);
        return {
            id: pos.id,
            x: pos.x,
            y: pos.y,
            w: src?.w || ENTITY_CARD_WIDTH,
            h: src?.h || ENTITY_CARD_MAX_HEIGHT,
        };
    });
    const packedBounds = cardsBoundingBox(packedCards);
    if (bounds.w > packedBounds.w * 1.55 || bounds.h > packedBounds.h * 1.55) return false;
    return true;
}

/**
 * @param {import('tldraw').Editor} editor
 * @param {Array<{id: string, x: number, y: number}>} positions
 */
export function applyCardPositions(editor, positions) {
    if (!positions.length) return;
    editor.updateShapes?.(
        positions.map((pos) => ({
            id: pos.id,
            type: ENTITY_CARD_SHAPE_TYPE,
            x: pos.x,
            y: pos.y,
        }))
    );
}

/**
 * Push connected card pairs apart when centers are closer than the label needs.
 * Caps per-card displacement so the compact pack does not explode into a wide strip.
 *
 * Shared between the legacy Tidy pack ({@link layoutChartHubSquare}) and the
 * pluggable layout runner ({@link module:layouts/chartLayoutRunner}) — both
 * apply positions first, then call this to spread any pair whose centers
 * landed closer together than their edge label needs.
 *
 * @param {import('tldraw').Editor} editor
 * @param {{maxPush?: number}} [options] `maxPush` overrides the default cap
 *   (tuned for the compact hub-square pack); layout engines that spread nodes
 *   over a much larger area may need a larger cap so a single long label does
 *   not get silently clamped back into overlap.
 */
export function ensureReadableEdgeLengths(editor, options = {}) {
    const maxPush = Number.isFinite(options.maxPush) ? options.maxPush : MAX_EDGE_LENGTH_PUSH;
    const arrows = (editor.getCurrentPageShapes?.() || []).filter((s) => s?.type === 'arrow');
    if (arrows.length === 0) return;

    const originById = new Map();
    const updatesById = new Map();
    for (const arrow of arrows) {
        const bindings = getArrowBindings(editor, arrow) || {};
        const startId = bindings.start?.toId;
        const endId = bindings.end?.toId;
        if (!startId || !endId) continue;

        const fromRect = shapeRect(editor, startId);
        const toRect = shapeRect(editor, endId);
        if (!fromRect || !toRect) continue;

        const label = arrowLabelTextFromShape(arrow) || '';
        const minLen = estimateMinEdgeLengthForLabel(label);
        const fromCx = fromRect.x + fromRect.w / 2;
        const fromCy = fromRect.y + fromRect.h / 2;
        const toCx = toRect.x + toRect.w / 2;
        const toCy = toRect.y + toRect.h / 2;
        const dx = toCx - fromCx;
        const dy = toCy - fromCy;
        const dist = Math.hypot(dx, dy);
        if (dist >= minLen || dist < 1e-3) continue;

        const scale = (minLen - dist) / (2 * dist);
        const pushX = dx * scale;
        const pushY = dy * scale;

        const fromShape = editor.getShape?.(startId);
        const toShape = editor.getShape?.(endId);
        if (fromShape) {
            if (!originById.has(startId)) {
                originById.set(startId, {x: fromShape.x, y: fromShape.y});
            }
            const prev = updatesById.get(startId) || {x: fromShape.x, y: fromShape.y};
            updatesById.set(startId, {x: prev.x - pushX, y: prev.y - pushY});
        }
        if (toShape) {
            if (!originById.has(endId)) {
                originById.set(endId, {x: toShape.x, y: toShape.y});
            }
            const prev = updatesById.get(endId) || {x: toShape.x, y: toShape.y};
            updatesById.set(endId, {x: prev.x + pushX, y: prev.y + pushY});
        }
    }

    if (updatesById.size === 0) return;
    editor.updateShapes?.(
        [...updatesById.entries()].map(([id, pos]) => {
            const origin = originById.get(id) || pos;
            let x = pos.x;
            let y = pos.y;
            const dx = x - origin.x;
            const dy = y - origin.y;
            const push = Math.hypot(dx, dy);
            if (push > maxPush) {
                const scale = maxPush / push;
                x = origin.x + dx * scale;
                y = origin.y + dy * scale;
            }
            return {
                id,
                type: ENTITY_CARD_SHAPE_TYPE,
                x,
                y,
            };
        })
    );
}

/**
 * Push endpoint cards apart along one axis so a routed segment can host its label.
 * Sibling of {@link ensureReadableEdgeLengths}: accumulate then cap, but only
 * move x for a horizontal deficit and only y for a vertical one.
 *
 * @param {import('tldraw').Editor} editor
 * @param {Array<{startId: string, endId: string, axis: 'x'|'y'|'h'|'v', deficit: number}>} deficits
 * @param {{maxPush?: number}} [options]
 */
export function lengthenEdgesForLabels(editor, deficits, options = {}) {
    const maxPush = Number.isFinite(options.maxPush) ? options.maxPush : MAX_EDGE_LENGTH_PUSH;
    if (!editor || !deficits?.length) return;

    const originById = new Map();
    const updatesById = new Map();

    for (const item of deficits) {
        const startId = item.startId;
        const endId = item.endId;
        const deficit = Number(item.deficit);
        if (!startId || !endId || !Number.isFinite(deficit) || deficit <= 0) continue;

        const fromShape = editor.getShape?.(startId);
        const toShape = editor.getShape?.(endId);
        if (!fromShape || !toShape) continue;

        const axis = (item.axis === 'y' || item.axis === 'v') ? 'y' : 'x';
        const fromCoord = axis === 'x' ? fromShape.x : fromShape.y;
        const toCoord = axis === 'x' ? toShape.x : toShape.y;
        const offsetCorridor = item.reason === 'corridor-offset';
        const fromSign = offsetCorridor
            ? (item.sign < 0 ? -1 : 1)
            : (fromCoord <= toCoord ? -1 : 1);
        const toSign = offsetCorridor ? fromSign : -fromSign;
        const push = deficit / 2;

        const bump = (id, shape, sign) => {
            if (!originById.has(id)) {
                originById.set(id, {x: shape.x, y: shape.y});
            }
            const prev = updatesById.get(id) || {x: shape.x, y: shape.y};
            updatesById.set(id, {
                x: prev.x + (axis === 'x' ? sign * push : 0),
                y: prev.y + (axis === 'y' ? sign * push : 0),
            });
        };
        bump(startId, fromShape, fromSign);
        bump(endId, toShape, toSign);
    }

    if (updatesById.size === 0) return;
    editor.updateShapes?.(
        [...updatesById.entries()].map(([id, pos]) => {
            const origin = originById.get(id) || pos;
            let x = pos.x;
            let y = pos.y;
            const dx = x - origin.x;
            const dy = y - origin.y;
            const push = Math.hypot(dx, dy);
            if (push > maxPush) {
                const scale = maxPush / push;
                x = origin.x + dx * scale;
                y = origin.y + dy * scale;
            }
            return {
                id,
                type: ENTITY_CARD_SHAPE_TYPE,
                x,
                y,
            };
        })
    );
}

/**
 * Light post-pass: clear card–card overlaps and re-route arrows without
 * packing into a new near-square grid. Prefer {@link layoutChartHubSquare}
 * for Pure LLM post-apply / tidy — this path is only for local shove.
 *
 * @param {import('tldraw').Editor} editor
 * @param {{edgeStyle?: 'straight'|'curved'|'elbow'}} [options]
 * @returns {{moved: number, packed: boolean}}
 */
export function clearChartCardOverlaps(editor, options = {}) {
    if (!editor) {
        return {moved: 0, packed: false};
    }

    const before = collectEntityCards(editor);
    if (before.length === 0) {
        return {moved: 0, packed: false};
    }

    resolveEntityCardOverlaps(editor, {maxPasses: 32, pad: PLACEMENT_GAP});
    const edgeStyle = options.edgeStyle;
    if (edgeStyle === 'straight' || edgeStyle === 'curved' || edgeStyle === 'elbow') {
        applyChartEdgeStyle(editor, edgeStyle);
    } else {
        const edges = collectCardEdges(editor);
        const arcOnly = edges.length <= 4;
        rerouteAllChartArrows(editor, null, {useArc: true, preferStraight: true, arcOnly});
    }

    const after = collectEntityCards(editor);
    let moved = 0;
    const beforeById = new Map(before.map((c) => [c.id, c]));
    for (const card of after) {
        const prev = beforeById.get(card.id);
        if (!prev) continue;
        if (Math.abs(prev.x - card.x) > 0.5 || Math.abs(prev.y - card.y) > 0.5) {
            moved += 1;
        }
    }

    return {moved, packed: false};
}

/**
 * Deterministic hub-centric compact layout with a near-square footprint.
 *
 * Trusts graph topology (and optional hubRef / goal keywords) — ignores LLM x/y.
 * Places the hub at the chart centroid, satellites on multi-rings / capped rings,
 * clears card overlaps, then reroutes arrows (straight when clear, curved otherwise).
 *
 * @param {import('tldraw').Editor} editor
 * @param {{hubRef?: string|null, goalKeywords?: string[], gap?: number}} [options]
 * @returns {{moved: number, packed: boolean, hubId: string|null}}
 */
export function layoutChartHubSquare(editor, options = {}) {
    if (!editor) {
        return {moved: 0, packed: false, hubId: null};
    }

    const before = collectEntityCards(editor);
    if (before.length === 0) {
        return {moved: 0, packed: false, hubId: null};
    }

    const edges = collectCardEdges(editor);
    const gap = Number.isFinite(options.gap) ? options.gap : PLACEMENT_GAP;
    const hubId = pickChartHubId(before, edges, {
        hubRef: options.hubRef,
        goalKeywords: options.goalKeywords,
    });
    if (!hubId) {
        return {moved: 0, packed: false, hubId: null};
    }

    if (before.length === 1) {
        dedupeFanInArrowLabels(editor);
        rerouteAllChartArrows(editor, null, {useArc: true, preferStraight: true});
        return {moved: 0, packed: false, hubId};
    }

    const {gapH, gapV} = layoutCardGaps(gap);
    const overlapPad = Math.max(gapH, gapV);

    const bounds = cardsBoundingBox(before);
    let centerX = bounds.minX + bounds.w / 2;
    let centerY = bounds.minY + bounds.h / 2;
    const container = editor.getContainer?.();
    if (container && typeof editor.screenToPage === 'function') {
        try {
            const rect = container.getBoundingClientRect();
            const center = editor.screenToPage({
                x: rect.left + rect.width / 2,
                y: rect.top + rect.height / 2,
            });
            if (Number.isFinite(center?.x) && Number.isFinite(center?.y)) {
                centerX = center.x;
                centerY = center.y;
            }
        } catch {
            // Keep AABB centroid.
        }
    }

    let packed = false;
    for (let attempt = 0; attempt < 14; attempt += 1) {
        const cards = collectEntityCards(editor);
        const positions = computeHubSquarePositions(
            cards,
            hubId,
            edges,
            gap,
            centerX,
            centerY,
            attempt * 48,
        );
        applyCardPositions(editor, positions);
        packed = true;
        resolveEntityCardOverlaps(editor, {maxPasses: 48, pad: overlapPad});
        const after = collectEntityCards(editor);
        if (!cardsHaveOverlaps(after, overlapPad) && !isStripLayout(after, STRIP_ASPECT_RATIO)) {
            break;
        }
    }

    resolveEntityCardOverlaps(editor, {maxPasses: 32, pad: overlapPad});
    dedupeFanInArrowLabels(editor);
    rerouteAllChartArrows(editor, null, {useArc: true, preferStraight: true});

    const after = collectEntityCards(editor);
    let moved = 0;
    const beforeById = new Map(before.map((c) => [c.id, c]));
    for (const card of after) {
        const prev = beforeById.get(card.id);
        if (!prev) continue;
        if (Math.abs(prev.x - card.x) > 0.5 || Math.abs(prev.y - card.y) > 0.5) {
            moved += 1;
        }
    }

    return {moved, packed, hubId};
}

/**
 * Compact the open chart into a near-square layout without card overlaps.
 *
 * @param {import('tldraw').Editor} editor
 * @returns {{moved: number, packed: boolean}}
 */
export function compactChartLayout(editor) {
    if (!editor) {
        return {moved: 0, packed: false};
    }

    const cards = collectEntityCards(editor);
    if (cards.length === 0) {
        return {moved: 0, packed: false};
    }

    const edges = collectCardEdges(editor);
    let packed = false;
    if (cards.length >= 2) {
        const localOk = shouldPreferLocalRepulsion(cards, PLACEMENT_GAP, edges);
        if (!localOk) {
            let gap = PLACEMENT_GAP;
            if (edges.some((e) => (e.label || '').length >= 12)) {
                gap = Math.max(gap, 110);
            }
            let positions = packCardsNearSquare(cards, gap, edges);
            positions = reducePackCrossingsBySwaps(positions, cards, edges);
            applyCardPositions(editor, positions);
            packed = true;
        }
    }

    resolveEntityCardOverlaps(editor, {maxPasses: 48, pad: PLACEMENT_GAP});
    ensureReadableEdgeLengths(editor);
    resolveEntityCardOverlaps(editor, {maxPasses: 24, pad: PLACEMENT_GAP});

    // If edge-through-card mess remains after local nudges, force a denser pack once.
    const afterNudge = collectEntityCards(editor);
    const afterEdges = collectCardEdges(editor);
    const mess = scoreStraightEdgeMess(afterNudge, afterEdges);
    if (!packed && (mess.cardHits > 0 || mess.crossings > 1) && afterNudge.length >= 2) {
        let positions = packCardsNearSquare(afterNudge, PLACEMENT_GAP + 20, afterEdges);
        positions = reducePackCrossingsBySwaps(positions, afterNudge, afterEdges);
        applyCardPositions(editor, positions);
        resolveEntityCardOverlaps(editor, {maxPasses: 32, pad: PLACEMENT_GAP});
        packed = true;
    }

    dedupeFanInArrowLabels(editor);
    // Prefer elbow when many edges — arcs cross more on dense packs.
    const arcOnly = afterEdges.length <= 4;
    rerouteAllChartArrows(editor, null, {useArc: true, preferStraight: true, arcOnly});

    const after = collectEntityCards(editor);
    let moved = 0;
    const beforeById = new Map(cards.map((c) => [c.id, c]));
    for (const card of after) {
        const prev = beforeById.get(card.id);
        if (!prev) continue;
        if (Math.abs(prev.x - card.x) > 0.5 || Math.abs(prev.y - card.y) > 0.5) {
            moved += 1;
        }
    }

    return {moved, packed};
}

export default compactChartLayout;
